import { HttpErrorResponse } from '@angular/common/http';
import {
    getFallbackMessage,
    getTrimmedStringValue,
    isProblemDetails,
    normalizeToProblemDetails,
    toProblemToastCopy,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import { catchError, from, map, of, type Observable } from 'rxjs';

/**
 * Convert every interceptor error shape into ProblemDetails without losing
 * useful server copy from blobs, buffers, plain strings, or common JSON bodies.
 */
export function toProblemDetails(
    err: unknown
): Observable<ResolvedProblemDetails> {
    if (!(err instanceof HttpErrorResponse)) {
        return of(normalizeToProblemDetails(err));
    }

    if (err.error instanceof Blob) {
        return from(readBlobProblemDetails(err.error)).pipe(
            map((body) => resolveHttpProblemDetails(err, body)),
            catchError(() => of(resolveHttpProblemDetails(err)))
        );
    }

    if (err.error instanceof ArrayBuffer) {
        return of(
            resolveHttpProblemDetails(err, readArrayBufferError(err.error))
        );
    }

    return of(resolveHttpProblemDetails(err));
}

/**
 * Flatten resolved ProblemDetails into the legacy one-string surface used by
 * suppressed-auto-toast call sites.
 */
export function getErrorMessage(
    problemDetails: ResolvedProblemDetails
): string {
    const copy = toProblemToastCopy(problemDetails);

    return copy.description
        ? [copy.message, copy.description].join('\n')
        : copy.message;
}

function resolveHttpProblemDetails(
    err: HttpErrorResponse,
    errorBody: unknown = err.error
): ResolvedProblemDetails {
    if (isProblemDetails(errorBody)) {
        const status = getStatus(err, errorBody.status);

        return withResponseMetadata(err, {
            ...errorBody,
            status,
            title:
                getTrimmedStringValue(errorBody.title) ??
                getFallbackMessage(status),
            detail: getTrimmedStringValue(errorBody.detail),
            instance:
                getTrimmedStringValue(errorBody.instance) ??
                err.url ??
                undefined,
        });
    }

    const status = err.status;
    const fallbackTitle = getFallbackMessage(status);

    // Angular reports JSON parse failures as HttpErrorResponse even for 2xx
    // responses; keep the toast actionable instead of exposing parser text.
    if (isJsonParseError(err)) {
        return withResponseMetadata(err, {
            status,
            title: 'Unexpected response from server.',
            detail: 'Please try again.',
            instance: err.url || undefined,
        });
    }

    if (typeof errorBody === 'string') {
        const detail = getStringBodyDetail(errorBody);

        return withResponseMetadata(err, {
            status,
            title: fallbackTitle,
            detail,
            instance: err.url || undefined,
        });
    }

    const objectBodyDetail = getObjectBodyDetail(errorBody);
    if (objectBodyDetail) {
        return withResponseMetadata(err, {
            status,
            title: fallbackTitle,
            detail: objectBodyDetail,
            instance: err.url || undefined,
        });
    }

    return withResponseMetadata(err, {
        status,
        title: fallbackTitle,
        instance: err.url || undefined,
    });
}

function withResponseMetadata(
    err: HttpErrorResponse,
    problemDetails: ResolvedProblemDetails
): ResolvedProblemDetails {
    const retryAfter = getTrimmedStringValue(err.headers.get('Retry-After'));

    return retryAfter ? { ...problemDetails, retryAfter } : problemDetails;
}

async function readBlobProblemDetails(blob: Blob): Promise<unknown> {
    if (!isJsonContentType(blob.type)) {
        throw new Error('Blob error body is not JSON.');
    }

    const text = await blob.text();
    return JSON.parse(text) as unknown;
}

function readArrayBufferError(value: ArrayBuffer): unknown {
    const text = new TextDecoder().decode(value).trim();
    if (!text) {
        return undefined;
    }

    try {
        return JSON.parse(text) as unknown;
    } catch {
        return undefined;
    }
}

function getStringBodyDetail(value: string): string | undefined {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return undefined;
    }

    if (isLikelyMarkup(trimmedValue)) {
        return extractHtmlTitle(trimmedValue);
    }

    return trimmedValue;
}

function extractHtmlTitle(value: string): string | undefined {
    const titleMatch = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(value);
    if (!titleMatch) return undefined;

    const title = titleMatch[1]?.replace(/\s+/g, ' ').trim();
    return title || undefined;
}

function getStatus(err: HttpErrorResponse, bodyStatus: unknown): number {
    if (err.status !== 0) {
        return err.status;
    }

    return typeof bodyStatus === 'number' ? bodyStatus : err.status;
}

function getObjectBodyDetail(errorBody: unknown): string | undefined {
    if (!isObjectRecord(errorBody)) {
        return undefined;
    }

    const message =
        getObjectStringDetail(errorBody['message']) ??
        getObjectStringDetail(errorBody['error_description']);
    if (message) {
        return message;
    }

    const nestedError = errorBody['error'];
    if (typeof nestedError === 'string') {
        return getStringBodyDetail(nestedError);
    }

    if (isObjectRecord(nestedError)) {
        return (
            getObjectStringDetail(nestedError['message']) ??
            getObjectStringDetail(nestedError['error_description'])
        );
    }

    return undefined;
}

function getObjectStringDetail(value: unknown): string | undefined {
    return typeof value === 'string' ? getStringBodyDetail(value) : undefined;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isJsonParseError(err: HttpErrorResponse): boolean {
    return (
        err.error instanceof SyntaxError ||
        (err.status >= 200 &&
            err.status < 300 &&
            err.message.toLocaleLowerCase().includes('parsing'))
    );
}

function isJsonContentType(value: string): boolean {
    const contentType = value.toLocaleLowerCase();

    return (
        contentType.includes('json') ||
        contentType.includes('application/problem')
    );
}

function isLikelyMarkup(value: string): boolean {
    return value.startsWith('<');
}
